import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { createBridge, type CaptureBridge, type PortTransport } from "../lib/captureBridge";
import { CONNECTING, CaptureContext, addToQueue, type CaptureValue } from "../lib/captureContext";
import { connectToExtension } from "../lib/chromeCapture";

/**
 * Holds one capture port open for the whole authenticated session.
 *
 * Mounted above the router in App, so the connection survives navigation: a run
 * is minutes long and its progress arrives as pushes, which a port rebuilt on
 * every route change would miss.
 *
 * `connect` is injectable for tests, which is the only reason a component this
 * thin takes a prop at all.
 */
export function CaptureProvider({
  children,
  connect = connectToExtension,
}: {
  children: ReactNode;
  connect?: () => PortTransport | null;
}) {
  // CONNECTING, never NO_CAPTURE: that one means "no provider above me" and its
  // `absent` is a settled verdict /refresh redirects on. Seeded from it, the
  // provider claimed that verdict on its first render and a direct load of
  // /refresh bounced to Points before the handshake could answer. See CONNECTING.
  const [state, setState] = useState(CONNECTING);
  const [finished, setFinished] = useState(0);
  const [queued, setQueued] = useState<string[]>([]);
  const [queueFailed, setQueueFailed] = useState(false);
  const [runAll, setRunAll] = useState(false);
  const [batch, setBatch] = useState<string[]>([]);
  const bridge = useRef<CaptureBridge | null>(null);
  // The effect below reads the queue when a run ends, and must see the newest.
  const queuedRef = useRef<string[]>([]);
  queuedRef.current = queued;

  useEffect(() => {
    const made = createBridge({
      connect,
      // The edge is detected in the bridge; this only counts it. Points and
      // Offers refetch off the count.
      onRunFinished: () => { setFinished((n) => n + 1); },
    });
    bridge.current = made;
    // A build made in a terminal shows up when the operator comes back to the
    // tab, without a button: the worker reads its disk on every hello.
    const onVisible = () => { if (document.visibilityState === "visible") made.recheck(); };
    document.addEventListener("visibilitychange", onVisible);
    // The bridge decides its first state synchronously -- a browser with no
    // extension API is `absent` before this effect returns -- so seed from it
    // rather than waiting for the first change that may never come.
    setState((previous) => ({ ...previous, ...made.state }));
    const off = made.subscribe((next) => {
      setState((previous) => ({ ...previous, ...next }));
    });
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      off();
      made.close();
      bridge.current = null;
    };
    // Once per mount. `connect` is a module function or a test's double, and
    // reconnecting because a prop identity changed would drop a live run's port.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lastRunId = state.snapshot?.last_run?.run_id;
  // A run just ended: it joins the batch, and the queue, if any, starts as one
  // run. Keyed on the finish count, so it fires once per run.
  useEffect(() => {
    if (finished === 0) return;
    if (lastRunId) setBatch((b) => (b.includes(lastRunId) ? b : [...b, lastRunId]));
    const slugs = queuedRef.current;
    if (slugs.length === 0 || !bridge.current) return;
    void bridge.current.start(slugs).then((answer) => {
      if ("error" in answer) {
        setQueueFailed(true);
      } else {
        setRunAll(false);
        setQueued((q) => q.filter((slug) => !slugs.includes(slug)));
        setQueueFailed(false);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finished]);

  // A run the operator starts from idle begins a new batch and replaces
  // whatever a refused queue left behind.
  const start = useCallback(async (slugs?: string[]) => {
    const answer = await (bridge.current?.start(slugs)
      ?? Promise.resolve({ error: "failed" as const }));
    if (!("error" in answer)) {
      setRunAll(slugs === undefined);
      setBatch([]);
      setQueued([]);
      setQueueFailed(false);
    }
    return answer;
  }, []);

  const value = useMemo<CaptureValue>(() => ({
    ...state,
    finished,
    queued,
    queueFailed,
    // Only while it is live: the flag outlasts the run otherwise.
    runAllLive: runAll && state.snapshot?.live === true,
    batch,
    enqueue: (slugs) => { setQueued((q) => addToQueue(q, slugs)); },
    clearQueue: () => { setQueued([]); setQueueFailed(false); },
    start,
    // Through the ref, so these keep working across a reconnect without the
    // identity of the callbacks changing on every snapshot.
    // Stop means stop: the queue does not start in its place.
    stop: async () => { setQueued([]); setQueueFailed(false); await bridge.current?.stop(); },
    reload: () => bridge.current?.reload() ?? Promise.resolve({ error: "failed" as const }),
    recheck: () => { bridge.current?.recheck(); },
    retry: () => { bridge.current?.retry(); },
  }), [state, finished, queued, queueFailed, runAll, batch, start]);

  return <CaptureContext.Provider value={value}>{children}</CaptureContext.Provider>;
}
